// The overview rail's persisted layout. Deliberately free of React so it type-checks and tests
// in the cheap `web/node` project.

export type OverviewRailPreference = {
  readonly order: readonly string[];
  /** Keyed by panel key; a key absent here is expanded. */
  readonly collapsed: Readonly<Record<string, boolean>>;
  /** Keyed by panel key; a key absent here is shown. Sections that can't be hidden never appear. */
  readonly hidden: Readonly<Record<string, boolean>>;
  /** The household nav's top-level tab order, keyed by tab title. Empty means the default order. */
  readonly tabOrder: readonly string[];
  /**
   * Household-only: whether Relationship holds the main column rather than the rail. `undefined`
   * means the advisor has never chosen, which the shell reads as expanded.
   */
  readonly relationshipExpanded: boolean | undefined;
};

// Frozen because one instance is shared by every rail that has nothing saved.
export const EMPTY_OVERVIEW_RAIL_PREFERENCE: OverviewRailPreference = Object.freeze({
  order: Object.freeze([]),
  collapsed: Object.freeze({}),
  hidden: Object.freeze({}),
  tabOrder: Object.freeze([]),
  relationshipExpanded: undefined,
});

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isStringArray(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

// A rail's key set changes at runtime (a panel that steps out while its full view is open) and
// between releases, so a saved order is never required to match the panels on screen — rendering
// skips keys it doesn't know and appends panels the order doesn't mention. Duplicates are the one
// thing that would render a panel twice, so they degrade the order to "nothing saved".
function parseUniqueStringArray(value: unknown): readonly string[] {
  if (!isStringArray(value)) return [];
  // Copied so the parsed preference owns its data — it goes into React state and is compared by
  // value, and an array aliased to the caller's would only bite once.
  return new Set(value).size === value.length ? [...value] : [];
}

// Panel keys are not validated against the panels on screen: the Relationship panel steps out
// while its full view is open, and pruning against the mounted set would drop its collapse state
// on every load where it isn't there.
function parseBooleanRecord(value: unknown): Record<string, boolean> {
  if (!isRecord(value)) return {};

  // Null-prototype so a stored `__proto__` key becomes an ordinary entry instead of hitting
  // Object.prototype's setter and being silently dropped.
  const record: Record<string, boolean> = Object.create(null);
  for (const [key, flag] of Object.entries(value)) {
    if (typeof flag === 'boolean') {
      record[key] = flag;
    }
  }
  return record;
}

/**
 * Accepts the bare `string[]` the key held before collapse state joined it, so an advisor's
 * existing saved order survives the shape change. One junk field degrades that field alone.
 * Returns null only when the payload is neither an array nor a map, which is the caller's cue to
 * keep what it already had rather than persist the garbage.
 */
export function parseOverviewRailPreference(value: unknown): OverviewRailPreference | null {
  if (Array.isArray(value)) {
    return {
      order: parseUniqueStringArray(value),
      collapsed: {},
      hidden: {},
      tabOrder: [],
      relationshipExpanded: undefined,
    };
  }
  if (!isRecord(value)) {
    return null;
  }
  return {
    order: parseUniqueStringArray(value['order']),
    collapsed: parseBooleanRecord(value['collapsed']),
    hidden: parseBooleanRecord(value['hidden']),
    tabOrder: parseUniqueStringArray(value['tabOrder']),
    relationshipExpanded:
      typeof value['relationshipExpanded'] === 'boolean' ? value['relationshipExpanded'] : undefined,
  };
}

function stringArraysEqual(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((key, index) => key === b[index]);
}

function booleanRecordsEqual(a: Readonly<Record<string, boolean>>, b: Readonly<Record<string, boolean>>): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  return aKeys.length === bKeys.length && aKeys.every((key) => a[key] === b[key]);
}

export function overviewRailPreferenceEquals(a: OverviewRailPreference, b: OverviewRailPreference): boolean {
  return (
    stringArraysEqual(a.order, b.order) &&
    booleanRecordsEqual(a.collapsed, b.collapsed) &&
    booleanRecordsEqual(a.hidden, b.hidden) &&
    stringArraysEqual(a.tabOrder, b.tabOrder) &&
    a.relationshipExpanded === b.relationshipExpanded
  );
}
